import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CalendarBlank } from '@phosphor-icons/react';

const KIND_STYLE = { holiday: 'bg-green-100 text-green-800', exam: 'bg-rose-100 text-rose-700', meeting: 'bg-blue-100 text-blue-700', sports: 'bg-amber-100 text-amber-800', cultural: 'bg-purple-100 text-purple-700', event: 'bg-orange-100 text-orange-700' };

export default function Events() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ['global_admin', 'school_admin', 'principal', 'registrar'].includes(user?.role);
  const [kind, setKind] = useState('all');
  const { data: events } = useQuery({ queryKey: ['events'], queryFn: () => api.get('/events').then((r) => r.data) });
  const [form, setForm] = useState({ title: '', kind: 'event', date: '', time: '', location: '', grades: '', rsvp_enabled: false });
  const inval = () => qc.invalidateQueries({ queryKey: ['events'] });
  const createMut = useMutation({
    mutationFn: () => api.post('/events', { ...form, grades: form.grades ? form.grades.split(',').map((g) => g.trim()) : [] }),
    onSuccess: () => { toast.success('Event published'); setForm({ ...form, title: '', date: '', time: '', location: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const rsvpMut = useMutation({
    mutationFn: ({ id, going }) => api.post(`/events/${id}/rsvp`, { going }),
    onSuccess: (r) => { toast.success(r.data.going ? 'See you there!' : 'Marked not attending'); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/events/${id}`),
    onSuccess: () => { toast.success('Event removed'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const editMut = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/events/${id}`, patch),
    onSuccess: () => { toast.success('Event updated'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const doEdit = (e) => {
    const title = window.prompt('Title:', e.title); if (!title) return;
    const date = window.prompt('Date (YYYY-MM-DD):', e.date) || e.date;
    const location = window.prompt('Location:', e.location || '') || '';
    editMut.mutate({ id: e.id, patch: { title, date, location } });
  };
  const filtered = (events || []).filter((e) => kind === 'all' || e.kind === kind);

  return (
    <div data-testid="events-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">Events & Calendar</h1>
      <p className="text-sm text-slate-500 mb-6">School events, holidays and RSVPs - visible to the right audience automatically</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {['all', 'event', 'holiday', 'exam', 'meeting', 'sports', 'cultural'].map((k) => (
          <button key={k} data-testid={`events-filter-${k}`} onClick={() => setKind(k)}
            className={`text-xs rounded-full px-3 py-1.5 border capitalize transition-colors ${kind === k ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:border-slate-900'}`}>{k}</button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-6" data-testid="events-list">
        {filtered.map((e) => (
          <div key={e.id} className="py-3 border-t border-slate-100 flex items-start gap-3 flex-wrap" data-testid={`event-row-${e.id}`}>
            <div className="text-center border border-slate-200 rounded-md px-2.5 py-1.5 shrink-0">
              <p className="font-display font-extrabold text-lg leading-none">{e.date.slice(8)}</p>
              <p className="font-mono-label text-[9px] uppercase text-slate-400">{new Date(e.date).toLocaleString('en', { month: 'short' })}</p>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm">{e.title} <span className={`ms-1 font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${KIND_STYLE[e.kind]}`}>{e.kind}</span></p>
              <p className="text-xs text-slate-500">{e.time && `${e.time} · `}{e.location || 'Campus'}{e.grades?.length ? ` · grades ${e.grades.join(', ')}` : ''}{e.rsvp_enabled ? ` · ${e.rsvp_count} RSVP` : ''}</p>
            </div>
            <span className="flex gap-2 items-center">
              {e.rsvp_enabled && (
                e.my_rsvp
                  ? <span className={`text-xs px-2 py-1 rounded ${e.my_rsvp.going ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`event-myrsvp-${e.id}`}>{e.my_rsvp.going ? 'Going ✓' : 'Not going'}</span>
                  : <>
                      <button data-testid={`event-rsvp-yes-${e.id}`} onClick={() => rsvpMut.mutate({ id: e.id, going: true })} className="text-xs bg-green-600 text-white rounded px-2.5 py-1 hover:bg-green-500">RSVP yes</button>
                      <button data-testid={`event-rsvp-no-${e.id}`} onClick={() => rsvpMut.mutate({ id: e.id, going: false })} className="text-xs border border-slate-200 rounded px-2.5 py-1">No</button>
                    </>
              )}
              {isAdmin && <button data-testid={`event-edit-${e.id}`} onClick={() => doEdit(e)} className="text-xs underline text-slate-400 hover:text-slate-900">edit</button>}
              {isAdmin && <button data-testid={`event-delete-${e.id}`} onClick={() => window.confirm(`Remove "${e.title}"?`) && delMut.mutate(e.id)} className="text-xs underline text-rose-500">remove</button>}
            </span>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-sm text-slate-400 py-4 flex items-center gap-2"><CalendarBlank size={16} /> No events yet.</p>}
      </div>

      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-md p-4 max-w-2xl" data-testid="event-create-panel">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Publish event</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <Input data-testid="event-title-input" placeholder="Title (Annual Sports Day)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <select data-testid="event-kind-select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
              {['event', 'holiday', 'exam', 'meeting', 'sports', 'cultural'].map((k) => <option key={k}>{k}</option>)}
            </select>
            <Input data-testid="event-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <Input data-testid="event-time-input" placeholder="Time (09:00 AM)" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
            <Input data-testid="event-location-input" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <Input data-testid="event-grades-input" placeholder="Grades (optional: 5, 6A)" value={form.grades} onChange={(e) => setForm({ ...form, grades: e.target.value })} />
            <label className="flex items-center gap-2 text-sm"><input data-testid="event-rsvp-toggle" type="checkbox" checked={form.rsvp_enabled} onChange={(e) => setForm({ ...form, rsvp_enabled: e.target.checked })} /> Collect RSVPs</label>
            <Button data-testid="event-create-button" className="bg-slate-900 hover:bg-slate-800" disabled={!form.title || !form.date || createMut.isPending} onClick={() => createMut.mutate()}>Publish</Button>
          </div>
        </div>
      )}
    </div>
  );
}
